(function () {
  const MODEL_CACHE = new Map();
  const MODAL_DIALOG_ID = 'SizeGuideModalDialog';
  const TRIGGER_SELECTOR = '[data-size-guide-open]';
  const PANEL_SELECTOR = '[data-size-guide-panel]';
  const TAB_SELECTOR = '[data-size-guide-tab]';
  const UNIT_SELECTOR = '[data-size-guide-unit]';
  const DEFAULT_UNIT = 'in';
  const EMPTY_MODEL_MAX_RETRIES = 3;
  const EMPTY_MODEL_RETRY_DELAY_MS = 350;
  const KIWI_SIZING_ENDPOINT = 'https://app.kiwisizing.com/api/getSizingChart';
  const KIWI_SETTINGS_CACHE_KEY_PREFIX = 'size-guide:kiwi-cache-settings-update-at:';
  const KIWI_SETTINGS_VERSION = '19';
  const BODY_GUIDE_FALLBACK_HTML = [
    '<p>We recommend measuring yourself with a soft tape measure, keeping it parallel to the floor and gently relaxed—not pulled taut.<br/><br/></p>',
    '<p>Bust: around the fullest part of the chest<br/><br/></p>',
    '<p>Waist: around the narrowest part of the midsection<br/><br/></p>',
    '<p>Hip: around the fullest part of the hips and seat</p>'
  ].join('');
  const GARMENT_GUIDE_FALLBACK_HTML = [
    '<p>We find it helpful to compare product measurements against something you already wear and love. Lay it flat on a smooth surface, measure straight across at the bust, waist, and hips, and multiply each by two for the full circumference. Use those numbers alongside the measurements listed above to find your best fit.</p>'
  ].join('');
  const BODY_SIZE_TABLE = {
    title: 'Body Size Guide',
    rows: [
      ['US SIZE', 'US SIZE', 'BUST', 'WAIST', 'HIPS'].map((value) => ({ value, type: 'header', preserveText: true })),
      ['00', 'XXS', ['30.5-31.5', '77.5 - 80'], ['23-24', '58.4 - 61'], ['34-35', '86.4 - 88.9']],
      ['0', 'XS', ['32.5', '82.5'], ['25', '63.5'], ['36', '91.4']],
      ['2', 'S', ['33.5', '85.1'], ['26', '66'], ['37', '94']],
      ['4', 'S', ['34.5', '87.6'], ['27', '68.6'], ['38', '96.5']],
      ['6', 'M', ['35.5', '90.2'], ['28', '71.1'], ['39', '99.1']],
      ['8', 'M', ['36.5', '92.7'], ['29', '73.7'], ['40', '102']],
      ['10', 'L', ['38.5-39', '97.8 - 99.1'], ['31-31.5', '78.7 - 80'], ['42-42.5', '107 - 108']],
      ['12', 'XL', ['41-41.5', '104 - 105'], ['33.5-34', '85.1 - 86.4'], ['44.5-45', '113 - 114']],
      ['14', 'XL', ['43', '109'], ['35.5', '90.2'], ['46.5', '118']],
      ['16', 'XXL', ['45-45.5', '114 - 116'], ['37.5-38', '95.3 - 96.5'], ['48.5-49', '123 - 124']]
    ].map((row, rowIndex) => rowIndex === 0
      ? row
      : row.map((value, index) => {
        if (index < 2) return { value, preserveText: true };

        return {
          value: value[0],
          unitType: 'in',
          convertedValues: {
            cm: value[1]
          }
        };
      }))
  };
  const CONVERSION_SIZE_TABLE = {
    title: 'Size Conversion Chart',
    rows: BODY_SIZE_TABLE.rows.map((row) => row.slice(0, 2))
  };

  function getNumericId(value) {
    if (value === null || value === undefined) return null;

    const normalizedValue = String(value).trim();
    if (!normalizedValue) return null;

    const match = normalizedValue.match(/(\d+)(?!.*\d)/);
    return match ? Number(match[1]) : null;
  }

  function parseJson(value) {
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('Failed to parse size guide context:', error);
      return null;
    }
  }

  function normalizeText(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object' && typeof value.value === 'string') return value.value;
    return '';
  }

  function normalizeListParam(value) {
    if (Array.isArray(value)) {
      return value.map(normalizeText).map((item) => item.trim()).filter(Boolean).join(',');
    }

    return normalizeText(value).replace(/\s*,\s*/g, ',').trim();
  }

  function normalizeBoolean(value) {
    return value === true || value === 'true';
  }

  function normalizeCategoryHandle(value) {
    return normalizeText(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function hasFilterCategory(categories, category) {
    const categoryHandle = normalizeCategoryHandle(category);
    if (!categoryHandle) return false;

    return normalizeListParam(categories)
      .split(',')
      .map(normalizeCategoryHandle)
      .includes(categoryHandle);
  }

  function getPlainText(html) {
    if (!html) return '';

    const template = document.createElement('template');
    template.innerHTML = html;
    return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function classifyHtmlBlock(html) {
    const text = getPlainText(html).toLowerCase();

    if (!text) return 'default';

    if (
      text.includes('measure your chest')
      || text.includes('measure your waist')
      || text.includes('measure your hips')
      || text.includes('fullest part of your chest')
      || text.includes('fullest part of your hips')
    ) {
      return 'body-guide';
    }

    if (
      text.includes('side seam to side seam')
      || text.includes('flat surface')
      || text.includes('flat across the garment')
      || text.includes('strap to hem')
      || text.includes('waist to hem')
      || text.includes('flare')
    ) {
      return 'garment-guide';
    }

    return 'default';
  }

  function formatDisplaySubtitle(value) {
    const normalizedValue = normalizeText(value).trim();
    if (!normalizedValue) return '';

    if (!/[a-z]/.test(normalizedValue) && /[A-Z]/.test(normalizedValue)) {
      return normalizedValue
        .toLowerCase()
        .replace(/\b([a-z])/g, (match) => match.toUpperCase());
    }

    return normalizedValue;
  }

  function formatTitleCaseLabel(value) {
    const normalizedValue = normalizeText(value).replace(/\s+/g, ' ').trim().toLowerCase();
    if (!normalizedValue) return '';

    return normalizedValue.replace(/\b([a-z])/g, (match) => match.toUpperCase());
  }

  function formatSizeLabel(value) {
    return normalizeText(value).replace(/\s+/g, ' ').trim().toUpperCase();
  }

  function getRegionLabel(value) {
    const normalizedValue = normalizeText(value)
      .replace(/\./g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    const regionLabels = {
      america: 'USA',
      aus: 'AUS',
      australia: 'AUS',
      britain: 'UK',
      ca: 'CANADA',
      can: 'CANADA',
      canada: 'CANADA',
      china: 'CHINA',
      cn: 'CHINA',
      de: 'GERMANY',
      eu: 'EU',
      eur: 'EU',
      europe: 'EU',
      'european union': 'EU',
      fr: 'FRANCE',
      france: 'FRANCE',
      gb: 'UK',
      germany: 'GERMANY',
      it: 'ITALY',
      italy: 'ITALY',
      japan: 'JAPAN',
      jp: 'JAPAN',
      kr: 'KOREA',
      korea: 'KOREA',
      nz: 'NEW ZEALAND',
      'new zealand': 'NEW ZEALAND',
      uk: 'UK',
      'united kingdom': 'UK',
      'united states': 'USA',
      'united states america': 'USA',
      'united states of america': 'USA',
      us: 'USA',
      usa: 'USA'
    };

    return regionLabels[normalizedValue] || '';
  }

  function formatTableLabel(value, isRegionCandidate = false) {
    const regionLabel = isRegionCandidate ? getRegionLabel(value) : '';
    if (regionLabel) return regionLabel;

    if (normalizeText(value).replace(/\s+/g, ' ').trim().toLowerCase() === 'us size') {
      return 'US Size';
    }

    return isSizeLabel(value)
      ? formatSizeLabel(value)
      : formatTitleCaseLabel(value);
  }

  function formatTableHeaderLabel(value) {
    const regionLabel = getRegionLabel(value);
    if (regionLabel && regionLabel.length <= 3) return regionLabel;

    if (normalizeText(value).replace(/\s+/g, ' ').trim().toLowerCase() === 'us size') {
      return 'US Size';
    }

    return isSizeLabel(value)
      ? formatSizeLabel(value)
      : formatTitleCaseLabel(value);
  }

  function shouldAdjustLeadingZeroSizeColumn(headerCell) {
    const headerText = normalizeText(getCellText(headerCell)).replace(/\s+/g, ' ').trim().toLowerCase();
    return headerText === 'usa' || headerText === 'us size';
  }

  function getConversionCellDisplayText(cellText, rowIndex, cellIndex, header, bodyRows) {
    if (
      rowIndex === 0
      && cellText === '0'
      && shouldAdjustLeadingZeroSizeColumn(header?.[cellIndex])
      && getCellText(bodyRows?.[rowIndex + 1]?.[cellIndex]) === '0'
    ) {
      return '00';
    }

    return cellText;
  }

  function isBraSizeLabel(value) {
    const normalizedValue = normalizeText(value).replace(/\s+/g, ' ').trim();
    return /^\d{2,3}[A-H]{1,3}$/i.test(normalizedValue);
  }

  function isSizeLabel(value) {
    const normalizedValue = normalizeText(value).replace(/\s+/g, ' ').trim();
    return isBraSizeLabel(normalizedValue) || /^(?:XXS|XS|S|M|L|XL|XXL|XXXL|OS|O\/S|\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?)$/i.test(normalizedValue);
  }

  function looksLikeDataRow(cell) {
    const cellText = getCellText(cell);

    if (!cellText) return false;

    return getMeasurementNumber(cellText) !== null;
  }

  function splitProductTitle(title) {
    const normalizedTitle = normalizeText(title).replace(/\s+/g, ' ').trim();
    if (!normalizedTitle) {
      return {
        title: 'Size Guide',
        subtitle: ''
      };
    }

    const parts = normalizedTitle.split(/\s+--\s+/);
    const primaryTitle = normalizeText(parts.shift()) || 'Size Guide';
    const subtitle = formatDisplaySubtitle(parts.join(' -- '));

    return {
      title: primaryTitle,
      subtitle
    };
  }

  function getTriggerAriaLabel(context) {
    const modal = document.getElementById('SizeGuideModal');
    const titleParts = splitProductTitle(context?.productTitle || '');
    const productName = [titleParts.title, titleParts.subtitle].filter(Boolean).join(', ');
    const baseLabel = modal?.dataset?.labelOpen || 'Open size guide';
    const withProductLabel = modal?.dataset?.labelOpenWithProduct || 'Open size guide for {product}';

    return productName && productName !== 'Size Guide'
      ? withProductLabel.replace('{product}', productName)
      : baseLabel;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function sanitizeHtml(html) {
    if (!html) return '';

    const allowedTags = new Set([
      'A', 'B', 'BR', 'DIV', 'EM', 'FIGURE', 'FIGCAPTION', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HR',
      'IMG', 'LI', 'OL', 'P', 'SPAN', 'STRONG', 'SUB', 'SUP', 'TABLE', 'TBODY', 'TD', 'TH', 'THEAD', 'TR', 'UL'
    ]);

    const template = document.createElement('template');
    template.innerHTML = html;

    template.content.querySelectorAll('*').forEach((element) => {
      if (!allowedTags.has(element.tagName)) {
        element.replaceWith(...element.childNodes);
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        const attributeName = attribute.name.toLowerCase();
        const attributeValue = attribute.value;

        if (attributeName.startsWith('on') || attributeName === 'style') {
          element.removeAttribute(attribute.name);
          return;
        }

        if (attributeName === 'href' && attributeValue.trim().toLowerCase().startsWith('javascript:')) {
          element.removeAttribute(attribute.name);
          return;
        }

        if (attributeName === 'target') {
          element.setAttribute('rel', 'noopener noreferrer');
        }
      });
    });

    return template.innerHTML;
  }

  function buildContext(input = {}) {
    const productId = getNumericId(input.productId || input.id || input.product?.id);
    if (!productId) return null;

    const filterCategories = normalizeListParam(input.filterCategories || input.filterCategory || input.filter_category);
    const isKids = hasFilterCategory(filterCategories, 'Kids');
    const isAccessories = hasFilterCategory(filterCategories, 'Accessories');
    const isBags = hasFilterCategory(filterCategories, 'Bags');
    const isDenim = hasFilterCategory(filterCategories, 'Denim');
    const isHome = hasFilterCategory(filterCategories, 'Home');
    const isShoes = hasFilterCategory(filterCategories, 'Shoes');
    const garmentTabLabel = isShoes ? 'Shoe' : ((isAccessories || isBags || isHome) ? 'Product' : '');

    return {
      productId,
      productTitle: normalizeText(input.productTitle || input.title),
      productTags: normalizeListParam(input.productTags || input.tags),
      productType: normalizeText(input.productType || input.type),
      productVendor: normalizeText(input.productVendor || input.vendor),
      productCollections: normalizeListParam(input.productCollections || input.collections),
      filterCategories,
      label: normalizeText(input.label) || 'SIZE & FIT',
      suppressBodyTab: normalizeBoolean(input.suppressBodyTab) || isKids || isAccessories || isDenim || isShoes,
      suppressMeasurementGuides: normalizeBoolean(input.suppressMeasurementGuides) || isKids || isAccessories || isShoes,
      onlyGarmentTab: normalizeBoolean(input.onlyGarmentTab) || isKids,
      garmentTabLabel: normalizeText(input.garmentTabLabel) || garmentTabLabel,
      metaobjectGuideCopy: {
        body: sanitizeHtml(normalizeText(input.metaobjectGuideCopy?.body || input.bodyGuideHtml)),
        garment: sanitizeHtml(normalizeText(input.metaobjectGuideCopy?.garment || input.garmentGuideHtml))
      },
      notes: {
        model: normalizeText(input.notes?.model || input.model),
        measurementInfo: normalizeText(input.notes?.measurementInfo || input.measurementInfo),
        fitNote: normalizeText(input.notes?.fitNote || input.fitNote)
      }
    };
  }

  function serializeContext(context) {
    return JSON.stringify(buildContext(context) || {});
  }

  function getContextFromElement(element) {
    return buildContext(parseJson(element?.dataset?.sizeGuideContext));
  }

  function getCellText(cell) {
    if (typeof cell === 'string') return cell.replace(/\s+/g, ' ').trim();
    if (typeof cell === 'number' || typeof cell === 'boolean') return String(cell);
    if (typeof cell?.value === 'string') return cell.value.replace(/\s+/g, ' ').trim();
    if (typeof cell?.value === 'number' || typeof cell?.value === 'boolean') return String(cell.value);
    return '';
  }

  function getMeasurementUnit(unitType) {
    return unitType === 'cm' || unitType === 'in' ? unitType : '';
  }

  function getMeasurementNumber(value) {
    if (value === null || value === undefined) return null;

    const normalizedValue = String(value).trim().replace(/,/g, '');
    if (!/^-?\d+(?:\.\d+)?$/.test(normalizedValue)) return null;

    return Number(normalizedValue);
  }

  function getMeasurementParts(value) {
    if (value === null || value === undefined) return null;

    const normalizedValue = String(value).trim().replace(/,/g, '');
    if (!/^-?\d+(?:\.\d+)?(?:\s*-\s*-?\d+(?:\.\d+)?)?$/.test(normalizedValue)) return null;

    const parts = normalizedValue.split(/\s*-\s*/).map(Number);
    return parts.every((part) => !Number.isNaN(part)) ? parts : null;
  }

  function trimTrailingZeros(value) {
    return String(value).replace(/0+$/, '').replace(/\.$/, '');
  }

  function formatConvertedMeasurement(value) {
    return trimTrailingZeros(value.toFixed(2));
  }

  function formatMeasurementValue(value, fromUnit, targetUnit = DEFAULT_UNIT) {
    const measurementParts = getMeasurementParts(value);
    if (!measurementParts) return getCellText({ value });

    return measurementParts.map((numericValue) => {
      let convertedValue = numericValue;
      if (fromUnit === 'in' && targetUnit === 'cm') {
        convertedValue = numericValue * 2.54;
      } else if (fromUnit === 'cm' && targetUnit === 'in') {
        convertedValue = numericValue / 2.54;
      }

      return formatConvertedMeasurement(convertedValue);
    }).join('-');
  }

  function hasConvertibleCells(tables) {
    return (Array.isArray(tables) ? tables : [tables]).some((table) => {
      if (!table || !Array.isArray(table.rows)) return false;

      return table.rows.some((row) => row.some((cell) => {
        const unitType = getMeasurementUnit(cell?.unitType);
        return unitType && getMeasurementParts(getCellText(cell)) !== null;
      }));
    });
  }

  function classifyTable(table) {
    const flattenedText = [table?.header || '']
      .concat((table?.data || []).flat().map(getCellText))
      .join(' ')
      .toLowerCase();

    if (
      flattenedText.includes('conversion')
      || flattenedText.includes('u.s.')
      || flattenedText.includes('u.k.')
      || flattenedText.includes('australia')
      || flattenedText.includes('france')
      || flattenedText.includes('italy')
      || flattenedText.includes('japan')
    ) {
      return 'conversion';
    }

    return 'garment';
  }

  function getSizingCollection(payload) {
    if (Array.isArray(payload?.sizings)) return payload.sizings;
    if (Array.isArray(payload?.data?.sizings)) return payload.data.sizings;
    if (payload?.data && typeof payload.data === 'object' && (payload.data.tables || payload.data.layout || payload.data.name)) {
      return [payload.data];
    }
    if (payload && typeof payload === 'object' && (payload.tables || payload.layout || payload.name)) {
      return [payload];
    }

    return [];
  }

  function getActiveSizing(payload) {
    const sizings = getSizingCollection(payload);
    return sizings.find((item) => item?.isEnabled !== false) || sizings[0] || null;
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function logKiwiDiagnostic(level, message, details) {
    const logger = typeof console?.[level] === 'function' ? console[level] : console.log;
    logger.call(console, `[SizeGuide] ${message}`, details);
  }

  function hasNonEmptyValue(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  }

  function getKiwiRequestBaseUrl(context, shop) {
    const requestUrl = new URL(KIWI_SIZING_ENDPOINT);
    requestUrl.searchParams.set('shop', shop);
    requestUrl.searchParams.set('title', context.productTitle);
    requestUrl.searchParams.set('tags', context.productTags);
    requestUrl.searchParams.set('type', context.productType);
    requestUrl.searchParams.set('product', String(context.productId));
    requestUrl.searchParams.set('vendor', context.productVendor);
    requestUrl.searchParams.set('collections', context.productCollections);
    requestUrl.searchParams.set('settingsVersion', KIWI_SETTINGS_VERSION);
    return requestUrl.toString();
  }

  function getKiwiSettingsStorageKey(requestBaseUrl) {
    return `${KIWI_SETTINGS_CACHE_KEY_PREFIX}${requestBaseUrl}`;
  }

  function readKiwiSettingsTimestamp(requestBaseUrl) {
    try {
      const value = window.localStorage.getItem(getKiwiSettingsStorageKey(requestBaseUrl));
      return typeof value === 'string' ? value.trim() : '';
    } catch (error) {
      return '';
    }
  }

  function writeKiwiSettingsTimestamp(requestBaseUrl, timestamp) {
    if (!timestamp) return;

    try {
      window.localStorage.setItem(getKiwiSettingsStorageKey(requestBaseUrl), timestamp);
    } catch (error) {
      // Ignore storage failures; the request can still proceed uncached.
    }
  }

  function removeKiwiSettingsTimestamp(requestBaseUrl) {
    try {
      window.localStorage.removeItem(getKiwiSettingsStorageKey(requestBaseUrl));
    } catch (error) {
      // Ignore storage failures; a fresh timestamp will still be used.
    }
  }

  function createKiwiSettingsTimestamp(previousTimestamp = '') {
    const previousTime = Date.parse(previousTimestamp);
    const now = Date.now();
    const nextTime = Number.isNaN(previousTime) ? now : Math.max(now, previousTime + 1);
    return new Date(nextTime).toISOString();
  }

  function buildKiwiRequestUrl(requestBaseUrl, cacheSettingsUpdateAt) {
    const requestUrl = new URL(requestBaseUrl);

    if (cacheSettingsUpdateAt) {
      requestUrl.searchParams.set('cacheSettingsUpdateAt', cacheSettingsUpdateAt);
    }

    return requestUrl.toString();
  }

  function fetchKiwiPayload(requestUrl) {
    return fetch(requestUrl, {
      credentials: 'omit',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load size guide: ${response.status}`);
        }

        return response.json();
      });
  }

  function createKiwiRequestResult(payload, context, details = {}) {
    const sizingCount = getSizingCollection(payload).length;
    const settingsPresent = hasNonEmptyValue(payload?.settings);
    const model = createModel(payload, context);

    if (!model.available) {
      logKiwiDiagnostic('warn', 'Kiwi returned no renderable size guide for request.', {
        attempt: details.attempt,
        productId: context.productId,
        productTitle: context.productTitle,
        sizingCount,
        settingsPresent,
        settingsId: payload?.settings?.id || null,
        cacheSettingsUpdateAt: details.cacheSettingsUpdateAt,
        reusedCachedSettingsTimestamp: details.reusedCachedSettingsTimestamp || false
      });
    }

    return {
      model,
      sizingCount,
      settingsPresent,
      cacheSettingsUpdateAt: details.cacheSettingsUpdateAt,
      reusedCachedSettingsTimestamp: details.reusedCachedSettingsTimestamp || false
    };
  }

  async function requestKiwiModel(context, requestBaseUrl, details = {}) {
    const payload = await fetchKiwiPayload(buildKiwiRequestUrl(requestBaseUrl, details.cacheSettingsUpdateAt));
    const result = createKiwiRequestResult(payload, context, details);

    if (result.settingsPresent && result.sizingCount > 0) {
      writeKiwiSettingsTimestamp(requestBaseUrl, result.cacheSettingsUpdateAt);
    }

    return result;
  }

  function getTableEntries(sizing) {
    const tables = sizing?.tables;
    if (!tables || typeof tables !== 'object') return [];

    return Object.entries(tables);
  }

  function getLayoutBlocks(sizing) {
    if (Array.isArray(sizing?.layout?.data)) return sizing.layout.data;
    if (Array.isArray(sizing?.layout)) return sizing.layout;
    if (Array.isArray(sizing?.blocks)) return sizing.blocks;
    return [];
  }

  function normalizeTable(table, key) {
    return {
      id: key,
      title: table?.header || '',
      rows: Array.isArray(table?.data) ? table.data : [],
      classification: classifyTable(table)
    };
  }

  function normalizeBlocks(sizing) {
    const layoutBlocks = getLayoutBlocks(sizing);
    const tableMapping = sizing?.layout?.templateTableMapping || {};

    return layoutBlocks.map((block, index) => {
      if (block?.type === 1) {
        const rawKey = block.value;
        const mappedKey = tableMapping[rawKey] || rawKey;
        const sourceTable = sizing?.tables?.[rawKey] || sizing?.tables?.[mappedKey];

        if (!sourceTable || sourceTable.hide) return null;

        const normalizedTable = normalizeTable(sourceTable, mappedKey);
        return {
          id: `table-${mappedKey}-${index}`,
          type: 'table',
          table: normalizedTable,
          classification: normalizedTable.classification
        };
      }

      if (block?.type === 0 && block.value) {
        return {
          id: `html-${index}`,
          type: 'html',
          html: sanitizeHtml(block.value),
          classification: classifyHtmlBlock(block.value)
        };
      }

      if (block?.type === 6 && block?.data?.url) {
        return {
          id: `image-${index}`,
          type: 'image',
          url: block.data.url,
          alt: '',
          classification: 'default'
        };
      }

      return null;
    }).filter(Boolean);
  }

  function normalizeFallbackTables(sizing, blocks) {
    const referencedTableIds = new Set(
      blocks
        .filter((block) => block.type === 'table' && block.table?.id)
        .map((block) => block.table.id)
    );

    return getTableEntries(sizing).map(([key, table]) => {
      if (!table || table.hide || referencedTableIds.has(key)) return null;

      const normalizedTable = normalizeTable(table, key);
      return {
        id: `fallback-table-${key}`,
        type: 'table',
        table: normalizedTable,
        classification: normalizedTable.classification
      };
    }).filter(Boolean);
  }

  function normalizeNotes(context) {
    const notes = [];
    const source = context?.notes || {};

    if (source.model) {
      notes.push({ title: 'Model', content: source.model });
    }

    if (source.measurementInfo) {
      notes.push({ title: 'Measurements', content: source.measurementInfo });
    }

    if (source.fitNote) {
      notes.push({ title: 'Fit Note', content: source.fitNote });
    }

    return notes;
  }

  function toGuideBlocks(blocks, prefix) {
    return blocks.map((block, index) => ({
      id: `${prefix}-${index}`,
      type: 'guide',
      html: block.html
    }));
  }

  function joinGuideHtml(blocks) {
    return blocks.map((block) => block.html).filter(Boolean).join('');
  }

  function createDefaultModel(context, titleSource = '') {
    const titleParts = splitProductTitle(titleSource || context?.productTitle || 'Size Guide');
    const suppressBodyTab = context?.suppressBodyTab || false;
    const onlyGarmentTab = context?.onlyGarmentTab || false;
    const tabs = [];

    if (!suppressBodyTab) {
      tabs.push({
        key: 'body',
        label: 'body',
        layout: 'body',
        table: BODY_SIZE_TABLE,
        guideHtml: context?.metaobjectGuideCopy?.body || BODY_GUIDE_FALLBACK_HTML,
        hasConvertibleUnits: hasConvertibleCells(BODY_SIZE_TABLE),
        suppressMeasurementGuides: context?.suppressMeasurementGuides || false
      });
    }

    if (!onlyGarmentTab) {
      tabs.push({
        key: 'conversion',
        label: 'conversion',
        layout: 'conversion',
        tables: [CONVERSION_SIZE_TABLE]
      });
    }

    if (!tabs.length) {
      return {
        available: false,
        title: titleParts.title,
        subtitle: titleParts.subtitle,
        tabs: []
      };
    }

    return {
      available: true,
      title: titleParts.title,
      subtitle: titleParts.subtitle,
      tabs
    };
  }

  function createModel(payload, context) {
    const sizing = getActiveSizing(payload);
    if (!sizing) {
      return createDefaultModel(context);
    }

    const normalizedBlocks = normalizeBlocks(sizing);
    const blocks = normalizedBlocks.concat(normalizeFallbackTables(sizing, normalizedBlocks));
    const garmentTables = blocks.filter((block) => block.type === 'table' && block.classification === 'garment');
    const conversionTables = blocks.filter((block) => block.type === 'table' && block.classification === 'conversion');
    const bodyGuideBlock = blocks.find((block) => block.type === 'html' && block.classification === 'body-guide');
    const garmentGuideBlocks = blocks.filter((block) => block.type === 'html' && block.classification === 'garment-guide');
    const fallbackGuideBlocks = blocks.filter((block) => block.type === 'html' && block.classification === 'default');
    const notes = normalizeNotes(context);
    const titleParts = splitProductTitle(context?.productTitle || sizing?.name || 'Size Guide');
    const bodyMetaobjectGuideHtml = context?.metaobjectGuideCopy?.body || '';
    const garmentMetaobjectGuideHtml = context?.metaobjectGuideCopy?.garment || '';
    const garmentGuideHtml = garmentMetaobjectGuideHtml || joinGuideHtml(garmentGuideBlocks.concat(fallbackGuideBlocks));
    const suppressBodyTab = context?.suppressBodyTab || false;
    const onlyGarmentTab = context?.onlyGarmentTab || false;

    const tabs = [];
    const primaryGarmentTable = garmentTables[0]?.table || null;

    if (!suppressBodyTab && (primaryGarmentTable || bodyGuideBlock)) {
      tabs.push({
        key: 'body',
        label: 'body',
        layout: 'body',
        table: primaryGarmentTable,
        guideHtml: bodyMetaobjectGuideHtml || bodyGuideBlock?.html || BODY_GUIDE_FALLBACK_HTML,
        hasConvertibleUnits: hasConvertibleCells(primaryGarmentTable),
        suppressMeasurementGuides: context?.suppressMeasurementGuides || false
      });
    }

    if (garmentTables.length || garmentGuideHtml || notes.length) {
      tabs.push({
        key: 'garment',
        label: context?.garmentTabLabel || 'garment',
        layout: 'garment',
        tables: garmentTables.map((block) => block.table),
        guideHtml: garmentGuideHtml || GARMENT_GUIDE_FALLBACK_HTML,
        notes,
        hasConvertibleUnits: hasConvertibleCells(garmentTables.map((block) => block.table)),
        suppressMeasurementGuides: context?.suppressMeasurementGuides || false
      });
    }

    if (!onlyGarmentTab && conversionTables.length) {
      tabs.push({
        key: 'conversion',
        label: 'conversion',
        layout: 'conversion',
        tables: conversionTables.map((block) => block.table)
      });
    }

    if (!tabs.length) {
      return createDefaultModel(context, sizing?.name);
    }

    return {
      available: true,
      title: titleParts.title,
      subtitle: titleParts.subtitle,
      tabs
    };
  }

  async function requestModel(context, shop, attempt = 1) {
    const requestBaseUrl = getKiwiRequestBaseUrl(context, shop);
    const cachedTimestamp = readKiwiSettingsTimestamp(requestBaseUrl);
    let result = await requestKiwiModel(context, requestBaseUrl, {
      attempt,
      cacheSettingsUpdateAt: cachedTimestamp || createKiwiSettingsTimestamp(),
      reusedCachedSettingsTimestamp: Boolean(cachedTimestamp)
    });

    if (!result.settingsPresent || result.sizingCount === 0) {
      const refreshedTimestamp = createKiwiSettingsTimestamp(result.cacheSettingsUpdateAt);
      removeKiwiSettingsTimestamp(requestBaseUrl);

      logKiwiDiagnostic('info', 'Refreshing Kiwi cacheSettingsUpdateAt after empty response.', {
        attempt,
        productId: context.productId,
        productTitle: context.productTitle,
        previousCacheSettingsUpdateAt: result.cacheSettingsUpdateAt,
        sizingCount: result.sizingCount,
        settingsPresent: result.settingsPresent,
        cacheSettingsUpdateAt: refreshedTimestamp
      });

      result = await requestKiwiModel(context, requestBaseUrl, {
        attempt,
        cacheSettingsUpdateAt: refreshedTimestamp,
        reusedCachedSettingsTimestamp: false
      });
    }

    return result;
  }

  async function fetchModel(context, shop, options = {}) {
    const cacheKey = String(context.productId);
    if (!options.force && MODEL_CACHE.has(cacheKey)) {
      return MODEL_CACHE.get(cacheKey);
    }

    const request = requestModel(context, shop, 1)
      .then(async (result) => {
        let model = result.model;

        if (!model.available && !options.skipRetry) {
          MODEL_CACHE.delete(cacheKey);

          for (let attempt = 2; attempt <= EMPTY_MODEL_MAX_RETRIES + 1; attempt += 1) {
            const delayMs = EMPTY_MODEL_RETRY_DELAY_MS * (attempt - 1);
            logKiwiDiagnostic('info', 'Retrying Kiwi size guide request after empty response.', {
              productId: context.productId,
              attempt,
              maxAttempts: EMPTY_MODEL_MAX_RETRIES + 1,
              delayMs
            });

            await wait(delayMs);
            model = (await requestModel(context, shop, attempt)).model;

            if (model.available) {
              logKiwiDiagnostic('info', 'Kiwi size guide recovered on retry.', {
                productId: context.productId,
                attempt,
                title: model.title,
                tabCount: model.tabs.length
              });
              break;
            }
          }
        }

        return model;
      })
      .then((model) => {
        if (!model.available) {
          MODEL_CACHE.delete(cacheKey);
        }

        return model;
      })
      .catch((error) => {
        MODEL_CACHE.delete(cacheKey);
        throw error;
      });

    MODEL_CACHE.set(cacheKey, request);
    return request;
  }

  function renderRichText(block) {
    return `<div class="size-guide-modal__richtext">${block.html}</div>`;
  }

  function renderGuide(block) {
    return `<section class="size-guide-modal__guide"><div class="size-guide-modal__guide-copy">${block.html}</div></section>`;
  }

  function renderImage(block) {
    return `<figure class="size-guide-modal__image"><img src="${escapeHtml(block.url)}" alt="${escapeHtml(block.alt || '')}" loading="lazy"></figure>`;
  }

  function getTableColumnCount(header, bodyRows) {
    return [header, ...(Array.isArray(bodyRows) ? bodyRows : [])].reduce((max, row) => {
      return Math.max(max, Array.isArray(row) ? row.length : 0);
    }, 0);
  }

  function renderTableColumnGroup(columnCount, groupClassName = '') {
    if (
      columnCount < 2
      || (!groupClassName.includes('--body') && !groupClassName.includes('--conversion'))
    ) {
      return '';
    }

    const columns = Array.from({ length: columnCount }, (_, index) => {
      const className = [
        'size-guide-modal__table-col',
        index < 2 ? 'size-guide-modal__table-col--matched' : ''
      ].filter(Boolean).join(' ');

      return `<col class="${className}">`;
    }).join('');

    return `<colgroup>${columns}</colgroup>`;
  }

  function renderTable(block, options = {}) {
    const table = block.table;
    const showHeading = options.showHeading !== false;
    const heading = showHeading && table.title ? `<h3 class="size-guide-modal__table-title">${escapeHtml(table.title)}</h3>` : '';
    const rows = Array.isArray(table.rows) ? table.rows : [];
    const headerRow = rows[0] || [];
    const hasHeaderRow = headerRow.length > 0 && headerRow.every((cell) => cell?.type === 'header');
    const effectiveHeader = hasHeaderRow
      ? headerRow
      : (options.assumeHeaderRow && headerRow.length > 0 && !looksLikeDataRow(headerRow[0]) ? headerRow : null);
    const bodyRows = effectiveHeader ? rows.slice(1) : rows;
    const tableClassName = ['size-guide-modal__table', options.tableClassName].filter(Boolean).join(' ');
    const groupClassName = ['size-guide-modal__table-group', options.groupClassName].filter(Boolean).join(' ');
    const activeUnit = options.unit || DEFAULT_UNIT;
    const fallbackCaption = options.fallbackCaption || (
      options.groupClassName?.includes('--body') ? 'Body measurements' :
      options.groupClassName?.includes('--garment') ? 'Garment measurements' :
      options.groupClassName?.includes('--conversion') ? 'Size conversion chart' :
      'Size guide'
    );
    const captionText = table.title || fallbackCaption;
    const columnGroup = renderTableColumnGroup(getTableColumnCount(effectiveHeader, bodyRows), options.groupClassName);

    const renderCell = (cell, scopeType, isFirstColumn = false, isHeaderRow = false, overrideText = null) => {
      const cellText = getCellText(cell);
      let displayText = overrideText ?? cellText;
      const unitType = getMeasurementUnit(cell?.unitType);
      const measurementParts = getMeasurementParts(cellText);

      if (isHeaderRow && scopeType && !unitType) {
        displayText = formatTableHeaderLabel(cellText);
      } else if (!unitType && isBraSizeLabel(cellText)) {
        displayText = formatSizeLabel(cellText);
      } else if (cell?.preserveText) {
        displayText = cellText;
      } else if (scopeType && !unitType) {
        displayText = formatTableLabel(cellText, isHeaderRow || isFirstColumn);
      } else if (!unitType && isFirstColumn) {
        displayText = getRegionLabel(cellText) || cellText;
      }

      const convertedCmValue = cell?.convertedValues?.cm;
      const cmDataAttribute = convertedCmValue ? ` data-size-guide-cm-value="${escapeHtml(convertedCmValue)}"` : '';
      const dataAttributes = unitType && measurementParts
        ? ` data-size-guide-value="${escapeHtml(cellText)}" data-size-guide-unit-type="${unitType}"${cmDataAttribute}`
        : '';
      const content = escapeHtml(unitType ? formatMeasurementValue(cellText, unitType, activeUnit) : displayText);

      if (scopeType) {
        return `<th scope="${scopeType}"${dataAttributes}>${content}</th>`;
      }

      return `<td${dataAttributes}>${content}</td>`;
    };

    const thead = effectiveHeader
      ? `<thead><tr>${effectiveHeader.map((cell, index) => renderCell(cell, 'col', index === 0, true)).join('')}</tr></thead>`
      : '';

    const tbody = bodyRows.map((row, rowIndex) => {
      const cells = row.map((cell, index) => {
        const scopeType = index === 0 && (effectiveHeader || cell?.type === 'header') ? 'row' : '';
        const overrideText = options.groupClassName?.includes('--conversion')
          ? getConversionCellDisplayText(getCellText(cell), rowIndex, index, effectiveHeader, bodyRows)
          : null;

        return renderCell(cell, scopeType, index === 0, false, overrideText);
      }).join('');

      return `<tr>${cells}</tr>`;
    }).join('');

    const content = `<div class="size-guide-modal__table-scroll"><table class="${tableClassName}"><caption class="visually-hidden">${escapeHtml(captionText)}</caption>${columnGroup}${thead}<tbody>${tbody}</tbody></table></div>`;

    if (options.headingPosition === 'before') {
      return `<section class="${groupClassName}">${heading}${content}</section>`;
    }

    return `<section class="${groupClassName}">${content}${heading}</section>`;
  }

  function renderUnits(activeUnit = DEFAULT_UNIT, controlsId = '') {
    const controlsAttribute = controlsId ? ` aria-controls="${controlsId}"` : '';

    return `<div class="size-guide-modal__units" role="group" aria-label="Measurement unit conversion"><button type="button" class="size-guide-modal__unit-button" data-size-guide-unit="in" aria-label="Show measurements in inches" aria-pressed="${activeUnit === 'in' ? 'true' : 'false'}"${controlsAttribute}>in</button><span class="size-guide-modal__units-separator" aria-hidden="true">|</span><button type="button" class="size-guide-modal__unit-button" data-size-guide-unit="cm" aria-label="Show measurements in centimeters" aria-pressed="${activeUnit === 'cm' ? 'true' : 'false'}"${controlsAttribute}>cm</button></div>`;
  }

  function renderNotes(block) {
    const items = block.notes.map((note) => {
      const formattedContent = escapeHtml(note.content).replace(/\n/g, '<br>');
      return `<article class="size-guide-modal__note"><h3 class="size-guide-modal__notes-title">${escapeHtml(note.title)}</h3><div class="size-guide-modal__note-text">${formattedContent}</div></article>`;
    }).join('');

    return `<section class="size-guide-modal__notes">${items}</section>`;
  }

  function renderBlock(block) {
    if (block.type === 'html') return renderRichText(block);
    if (block.type === 'guide') return renderGuide(block);
    if (block.type === 'image') return renderImage(block);
    if (block.type === 'table') return renderTable(block);
    if (block.type === 'notes') return renderNotes(block);
    return '';
  }

  function renderMeasureFigure() {
    return `<div class="size-guide-modal__measure-figure" aria-hidden="true"><div class="size-guide-modal__measure-dressform"></div></div>`;
  }

  function renderMeasureDivider() {
    return `<div class="size-guide-modal__measure-divider" aria-hidden="true"><span class="size-guide-modal__measure-divider-line"></span></div>`;
  }

  function renderBodyMeasureSection(guideHtml) {
    return `<section class="size-guide-modal__measure">${renderMeasureFigure()}${renderMeasureDivider()}<div class="size-guide-modal__measure-copy"><h3 class="size-guide-modal__measure-title">How to Measure</h3><div class="size-guide-modal__measure-text">${guideHtml || BODY_GUIDE_FALLBACK_HTML}</div></div></section>`;
  }

  function renderGarmentMeasureSection(guideHtml, notes) {
    const notesMarkup = notes?.length ? renderNotes({ notes }) : '';
    return `<section class="size-guide-modal__garment-measure">${renderMeasureFigure()}${renderMeasureDivider()}<div class="size-guide-modal__garment-copy"><div class="size-guide-modal__guide"><div class="size-guide-modal__guide-copy">${guideHtml || GARMENT_GUIDE_FALLBACK_HTML}</div></div>${notesMarkup}</div></section>`;
  }

  function renderTableCollection(tables, options = {}) {
    return tables.map((table) => renderTable({ type: 'table', table }, options)).join('');
  }

  function renderTableSection(tableMarkup, unitsMarkup = '', className = '', id = '') {
    if (!tableMarkup) return '';

    const sectionClassName = ['size-guide-modal__table-section', className].filter(Boolean).join(' ');
    const idAttribute = id ? ` id="${id}"` : '';
    return `<div class="${sectionClassName}"${idAttribute}>${tableMarkup}${unitsMarkup}</div>`;
  }

  function renderBodyPanel(tab, isActive) {
    const tableSectionId = `SizeGuideTableSection-${tab.key}`;
    const tableMarkup = renderTable({ type: 'table', table: BODY_SIZE_TABLE }, { showHeading: false, groupClassName: 'size-guide-modal__table-group--body', unit: DEFAULT_UNIT, assumeHeaderRow: true });
    const unitsMarkup = renderUnits(DEFAULT_UNIT, tableSectionId);

    const measureMarkup = tab.suppressMeasurementGuides ? '' : renderBodyMeasureSection(tab.guideHtml);

    return `<section class="size-guide-modal__panel size-guide-modal__panel--body" role="tabpanel" id="SizeGuidePanel-${tab.key}" aria-labelledby="SizeGuideTab-${tab.key}" data-size-guide-panel="${tab.key}"${isActive ? '' : ' hidden'}>${renderTableSection(tableMarkup, unitsMarkup, 'size-guide-modal__table-section--body', tableSectionId)}${measureMarkup}</section>`;
  }

  function renderGarmentPanel(tab, isActive) {
    const tableSectionId = `SizeGuideTableSection-${tab.key}`;
    const tableMarkup = renderTableCollection(tab.tables || [], { showHeading: false, groupClassName: 'size-guide-modal__table-group--garment', unit: DEFAULT_UNIT, assumeHeaderRow: true });
    const unitsMarkup = tab.hasConvertibleUnits ? renderUnits(DEFAULT_UNIT, tableSectionId) : '';

    const measureMarkup = tab.suppressMeasurementGuides ? '' : renderGarmentMeasureSection(tab.guideHtml, tab.notes || []);

    return `<section class="size-guide-modal__panel size-guide-modal__panel--garment" role="tabpanel" id="SizeGuidePanel-${tab.key}" aria-labelledby="SizeGuideTab-${tab.key}" data-size-guide-panel="${tab.key}"${isActive ? '' : ' hidden'}>${renderTableSection(tableMarkup, unitsMarkup, 'size-guide-modal__table-section--garment', tableSectionId)}${measureMarkup}</section>`;
  }

  function renderConversionPanel(tab, isActive) {
    const tableMarkup = renderTableCollection(tab.tables || [], { showHeading: false, headingPosition: 'before', groupClassName: 'size-guide-modal__table-group--conversion', assumeHeaderRow: true });

    return `<section class="size-guide-modal__panel size-guide-modal__panel--conversion" role="tabpanel" id="SizeGuidePanel-${tab.key}" aria-labelledby="SizeGuideTab-${tab.key}" data-size-guide-panel="${tab.key}"${isActive ? '' : ' hidden'}>${tableMarkup}</section>`;
  }

  function renderPanel(tab, isActive) {
    if (tab.layout === 'body') {
      return renderBodyPanel(tab, isActive);
    }

    if (tab.layout === 'garment') {
      return renderGarmentPanel(tab, isActive);
    }

    if (tab.layout === 'conversion') {
      return renderConversionPanel(tab, isActive);
    }

    return `<section class="size-guide-modal__panel" role="tabpanel" id="SizeGuidePanel-${tab.key}" aria-labelledby="SizeGuideTab-${tab.key}" data-size-guide-panel="${tab.key}"${isActive ? '' : ' hidden'}>${tab.blocks.map(renderBlock).join('')}</section>`;
  }

  function renderTabs(model) {
    const activeKey = model.tabs[0]?.key;
    const tabs = model.tabs.map((tab, index) => `<button type="button" class="size-guide-modal__tab" id="SizeGuideTab-${tab.key}" role="tab" aria-selected="${index === 0 ? 'true' : 'false'}" aria-controls="SizeGuidePanel-${tab.key}" tabindex="${index === 0 ? '0' : '-1'}" data-size-guide-tab="${tab.key}">${escapeHtml(tab.label)}</button>`).join('');
    const panels = model.tabs.map((tab) => renderPanel(tab, tab.key === activeKey)).join('');

    return `<div class="size-guide-modal__tabs" role="tablist" aria-label="Size guide tabs" aria-orientation="horizontal">${tabs}</div>${panels}`;
  }

  function setTriggerAccessibility(trigger, context) {
    if (!trigger) return;

    trigger.setAttribute('aria-controls', MODAL_DIALOG_ID);
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', getTriggerAriaLabel(context));
  }

  function syncExpandedTrigger(activeTrigger = null) {
    document.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', activeTrigger && trigger === activeTrigger ? 'true' : 'false');
    });
  }

  class SizeGuideModal extends ModalDialog {
    constructor() {
      super();
      this.dialog = this.querySelector('[role="dialog"]');
      this.titleElement = this.querySelector('[data-size-guide-title]');
      this.subtitleElement = this.querySelector('[data-size-guide-subtitle]');
      this.statusElement = this.querySelector('[data-size-guide-status]');
      this.contentElement = this.querySelector('[data-size-guide-content]');
      this.innerElement = this.querySelector('.size-guide-modal__inner');
      this.activeUnit = DEFAULT_UNIT;
      this.requestToken = 0;

      this.addEventListener('click', (event) => {
        const unitButton = event.target.closest(UNIT_SELECTOR);
        if (unitButton) {
          event.preventDefault();
          this.setUnit(unitButton.dataset.sizeGuideUnit);
          return;
        }

        const tab = event.target.closest(TAB_SELECTOR);
        if (!tab) return;

        this.activateTab(tab.dataset.sizeGuideTab, { focusTarget: tab });
      });

      this.addEventListener('keydown', (event) => {
        const activeTab = event.target.closest(TAB_SELECTOR);
        if (!activeTab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        const tabs = Array.from(this.querySelectorAll(TAB_SELECTOR));
        const currentIndex = tabs.indexOf(activeTab);
        let nextIndex = currentIndex;

        if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = tabs.length - 1;
        } else {
          nextIndex = event.key === 'ArrowLeft'
            ? (currentIndex > 0 ? currentIndex - 1 : tabs.length - 1)
            : (currentIndex < tabs.length - 1 ? currentIndex + 1 : 0);
        }

        event.preventDefault();
        this.activateTab(tabs[nextIndex].dataset.sizeGuideTab, { focusTarget: tabs[nextIndex] });
      });
    }

    refreshFocusTrap(elementToFocus = this.dialog) {
      if (!this.hasAttribute('open') || typeof trapFocus !== 'function') return;

      trapFocus(this, elementToFocus || this.dialog || this);
    }

    show(opener) {
      this.openedBy = opener;
      const popup = this.querySelector('.template-popup');
      this.setAttribute('open', '');
      if (popup) popup.loadContent();
      trapFocus(this, this.querySelector('[role="dialog"]'));
      window.pauseAllMedia();
    }

    hide() {
      syncExpandedTrigger();
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia();
    }

    openWithContext(context, opener) {
      this.activeContext = context;
      syncExpandedTrigger(opener || null);
      this.renderLoading(context);
      this.show(opener);

      const token = ++this.requestToken;
      window.SizeGuide.getModel(context, { force: true })
        .then((model) => {
          if (token !== this.requestToken) return;

          if (!model.available) {
            this.renderUnavailable(context);
            return;
          }

          this.renderModel(model);
        })
        .catch((error) => {
          console.error('Failed to render size guide:', error);
          if (token !== this.requestToken) return;
          this.renderError();
        });
    }

    renderLoading(context) {
      const titleParts = splitProductTitle(context?.productTitle || 'Size Guide');
      this.titleElement.textContent = titleParts.title;
      this.subtitleElement.textContent = titleParts.subtitle;
      this.statusElement.textContent = 'Loading size guide';
      this.contentElement.setAttribute('aria-busy', 'true');
      this.contentElement.innerHTML = '<div class="size-guide-modal__loading" role="status" aria-label="Loading size guide">Loading</div>';
    }

    renderUnavailable(context) {
      const titleParts = splitProductTitle(context?.productTitle || 'Size Guide');
      this.titleElement.textContent = titleParts.title;
      this.subtitleElement.textContent = titleParts.subtitle;
      this.statusElement.textContent = '';
      this.contentElement.setAttribute('aria-busy', 'false');
      this.contentElement.innerHTML = '<div class="size-guide-modal__empty">Size guide unavailable for this product.</div>';
      this.refreshFocusTrap(this.querySelector('[id^="ModalClose-"]') || this.dialog);
    }

    renderError() {
      this.statusElement.textContent = '';
      this.contentElement.setAttribute('aria-busy', 'false');
      this.contentElement.innerHTML = '<div class="size-guide-modal__error">Unable to load size guide right now.</div>';
      this.refreshFocusTrap(this.querySelector('[id^="ModalClose-"]') || this.dialog);
    }

    renderModel(model) {
      const activeElement = document.activeElement;
      const activeFocusTarget = activeElement instanceof HTMLElement && this.contains(activeElement)
        ? activeElement
        : null;
      const shouldFocusFirstTab = !activeFocusTarget || activeFocusTarget === this.dialog || activeFocusTarget === this;

      this.titleElement.textContent = model.title;
      this.subtitleElement.textContent = model.subtitle || '';
      this.statusElement.textContent = '';
      this.contentElement.setAttribute('aria-busy', 'false');
      this.contentElement.innerHTML = renderTabs(model);
      this.activeUnit = DEFAULT_UNIT;
      if (this.innerElement) {
        this.innerElement.scrollTop = 0;
      }
      this.activateTab(model.tabs[0]?.key, {
        focusTarget: shouldFocusFirstTab ? this.querySelector(TAB_SELECTOR) || this.dialog : activeFocusTarget
      });
      this.setUnit(this.activeUnit, false);
    }

    activateTab(key, options = {}) {
      if (!key) return;

      this.querySelectorAll(TAB_SELECTOR).forEach((tab) => {
        const isActive = tab.dataset.sizeGuideTab === key;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
        panel.hidden = panel.dataset.sizeGuidePanel !== key;
      });

      const fallbackFocusTarget = this.querySelector(`${TAB_SELECTOR}[data-size-guide-tab="${key}"]`) || this.dialog;
      const requestedFocusTarget = options.focusTarget;
      const focusTarget = requestedFocusTarget instanceof HTMLElement && requestedFocusTarget.isConnected && this.contains(requestedFocusTarget)
        ? requestedFocusTarget
        : fallbackFocusTarget;
      this.refreshFocusTrap(focusTarget);
    }

    setUnit(unit, announce = true) {
      if (unit !== 'cm' && unit !== 'in') return;

      this.activeUnit = unit;

      this.querySelectorAll(UNIT_SELECTOR).forEach((button) => {
        const isActive = button.dataset.sizeGuideUnit === unit;
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.querySelectorAll('[data-size-guide-value][data-size-guide-unit-type]').forEach((cell) => {
        const rawValue = cell.getAttribute('data-size-guide-value');
        const unitType = cell.getAttribute('data-size-guide-unit-type');
        const convertedCmValue = cell.getAttribute('data-size-guide-cm-value');
        cell.textContent = unit === 'cm' && convertedCmValue
          ? convertedCmValue
          : formatMeasurementValue(rawValue, unitType, unit);
      });

      this.statusElement.textContent = '';
    }
  }

  if (!customElements.get('size-guide-modal')) {
    customElements.define('size-guide-modal', SizeGuideModal);
  }

  async function hydrateTrigger(trigger) {
    const context = getContextFromElement(trigger);
    if (!context) {
      trigger.hidden = true;
      return;
    }

    setTriggerAccessibility(trigger, context);
    trigger.hidden = false;

    try {
      await window.SizeGuide.getModel(context);
    } catch (error) {
      console.error('Failed to initialize size guide trigger:', error);
    }
  }

  function bindTrigger(trigger) {
    const context = getContextFromElement(trigger);
    if (context) {
      setTriggerAccessibility(trigger, context);
    }

    if (trigger.dataset.sizeGuideBound === 'true') return;

    trigger.dataset.sizeGuideBound = 'true';
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      window.SizeGuide.openFromTrigger(trigger);
    });
  }

  window.SizeGuide = {
    buildContext,
    serializeContext,
    getContextFromElement,
    getModel(context, options = {}) {
      const modal = document.getElementById('SizeGuideModal');
      const shop = modal?.dataset?.shop;
      const normalizedContext = buildContext(context);

      if (!normalizedContext || !shop) {
        return Promise.resolve({ available: false, title: 'Size Guide', tabs: [] });
      }

      return fetchModel(normalizedContext, shop, options);
    },
    openFromTrigger(trigger) {
      const modal = document.getElementById('SizeGuideModal');
      if (!modal) return;

      const context = getContextFromElement(trigger);
      if (!context) return;

      modal.openWithContext(context, trigger);
    },
    refresh(scope = document) {
      scope.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
        bindTrigger(trigger);
        hydrateTrigger(trigger);
      });
    }
  };

  function initializeSizeGuide() {
    window.SizeGuide.refresh(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeSizeGuide);
  } else {
    initializeSizeGuide();
  }
})();
